import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Plus, Printer, Bus as BusIcon, Users, Calendar,
  Eye, XCircle, Filter, RefreshCw, Search, DollarSign, AlertCircle, CreditCard,
  Sun, Moon, ArrowRight, FileText, Pencil, MapPin, Navigation
} from 'lucide-react';
import BusEnrollModal from './modals/BusEnrollModal';
import BusRentalModal from './modals/BusRentalModal';
import GroupManagementModal from './modals/GroupManagementModal';
import BusCardPrintModal from './modals/BusCardPrintModal';
import BulkTrimesterTransferModal from './modals/BulkTrimesterTransferModal';
import BusBalanceReportModal from './modals/BusBalanceReportModal';
import BusByBusReportModal from './modals/BusByBusReportModal';
import BusEditEnrollmentModal from './modals/BusEditEnrollmentModal';
import BusDropoffPinsModal from './modals/BusDropoffPinsModal';
import BusLiveMapModal from './modals/BusLiveMapModal';
import { Gate } from './AccessStudio';

const Bus = ({ apiCall, selectedYear, currentTrimester, hasRole, hasPermission, showSuccess, showError }) => {
  // ============================================
  // PERMISSION CHECKS
  // Element visibility is now handled by <Gate>. These remaining vars are
  // only for structural logic (default tab, tab-content guards, admin tools).
  // ============================================
  const canViewEnrollments = hasPermission ? hasPermission('bus', 'student_enrollments', 'view') : true;
  const canViewRentals = hasPermission ? hasPermission('bus', 'group_rentals', 'view') : true;
  const canBulkTransfer = hasRole && hasRole('GlobalAdmin'); // Admin only

  // Active main tab state: 'enrollments' or 'rentals' - default based on permissions
  const [activeTab, setActiveTab] = useState(canViewEnrollments ? 'enrollments' : 'rentals');
  
  // Student Enrollments - Sub-tabs: 'list', 'statistics', 'lines'
  const [enrollmentSubTab, setEnrollmentSubTab] = useState('list');
  
  // Student Bus Data
  const [busLines, setBusLines] = useState([]);
  const [buses, setBuses] = useState([]);
  const [busEnrollments, setBusEnrollments] = useState([]);
  const [, setStudents] = useState([]);
  const [enrollmentStats, setEnrollmentStats] = useState(null);
  const [lineStats, setLineStats] = useState([]);

  // Admin-only enrolment edit (line/price/buses/addresses/discount)
  const [editEnrollment, setEditEnrollment] = useState(null);
  
  // Rental Data
  const [rentalGroups, setRentalGroups] = useState([]);
  const [rentalTrips, setRentalTrips] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Filters for student enrollments
  const [filters, setFilters] = useState({
    academicYear: selectedYear,
    trimester: currentTrimester?.toString() || '',
    lineId: '',
    status: '',
    paymentStatus: ''
  });
  
  // Separate search term for client-side filtering (no API call)
  const [searchTerm, setSearchTerm] = useState('');
  
  // UI States
  const [loading, setLoading] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
  const [showBalanceReport, setShowBalanceReport] = useState(false);
  const [showByBusReport, setShowByBusReport] = useState(false);
  const [showDropoffPins, setShowDropoffPins] = useState(false);
  const [showLiveMap, setShowLiveMap] = useState(false);
  const [detailsModal, setDetailsModal] = useState({ open: false, data: null });
  const [printCardModal, setPrintCardModal] = useState({ open: false, data: null });

  // Load Student Bus Data
  const loadEnrollmentData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params (searchTerm NOT included - client-side only)
      const params = new URLSearchParams();
      if (filters.academicYear) params.append('academicYear', filters.academicYear);
      if (filters.trimester) params.append('trimester', filters.trimester);
      if (filters.lineId) params.append('lineId', filters.lineId);
      if (filters.status) params.append('status', filters.status);
      
      const [linesData, busesData, enrollments, studentsData, stats, lineStatsData] = await Promise.all([
        apiCall('/bus/lines'),
        apiCall('/bus/buses'),
        apiCall(`/bus/enrollments?${params.toString()}`),
        apiCall(`/students?academicYear=${selectedYear}`),
        apiCall(`/bus/enrollment-stats?academicYear=${filters.academicYear}&trimester=${filters.trimester || ''}`),
        apiCall(`/bus/enrollments-by-line?academicYear=${filters.academicYear}&trimester=${filters.trimester || ''}`)
      ]);
      
      setBusLines(linesData);
      setBuses(busesData);
      setBusEnrollments(enrollments);
      setStudents(studentsData);
      setEnrollmentStats(stats);
      setLineStats(lineStatsData);
    } catch (error) {
      showError('Error loading bus data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, selectedYear, filters, showError]);

  // Load Rental Data
  const loadRentalData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupsData, tripsData, busesData] = await Promise.all([
        apiCall('/bus/rental-groups'),
        apiCall('/bus/rental-trips'),
        apiCall('/bus/buses')
      ]);
      
      setRentalGroups(groupsData);
      setRentalTrips(tripsData);
      setBuses(busesData);
    } catch (error) {
      showError('Error loading rental data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'enrollments') {
      loadEnrollmentData();
    } else {
      loadRentalData();
    }
  }, [activeTab, loadEnrollmentData, loadRentalData]);

  const handlePrintCard = async (enrollmentId) => {
    try {
      // Fetch full enrollment details including addresses
      const cardData = await apiCall(`/bus/enrollments/${enrollmentId}/card-data`);
      setPrintCardModal({ open: true, data: cardData });
    } catch (error) {
      showError('Failed to load card data: ' + error.message);
    }
  };

  const handleCardPrinted = async (enrollmentId) => {
    try {
      await apiCall(`/bus/enrollments/${enrollmentId}/print`, { method: 'POST' });
      showSuccess('Card marked as printed');
      setPrintCardModal({ open: false, data: null });
      loadEnrollmentData();
    } catch (error) {
      showError(error.message);
    }
  };

  const handleViewDetails = async (enrollment) => {
    try {
      const data = await apiCall(`/bus/enrollments/${enrollment.BusEnrollmentID}/card-data`);
      setDetailsModal({ 
        open: true, 
        data: { ...enrollment, ...data }
      });
    } catch (error) {
      showError('Failed to load details: ' + error.message);
    }
  };

  const handleCancelEnrollment = async (enrollmentId) => {
    if (!window.confirm('Are you sure you want to cancel this enrollment?')) {
      return;
    }
    
    const reason = window.prompt('Cancellation reason (optional):');
    
    try {
      await apiCall(`/bus/enrollments/${enrollmentId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'Cancelled', reason })
      });
      showSuccess('Enrollment cancelled successfully');
      loadEnrollmentData();
    } catch (error) {
      showError('Failed to cancel enrollment: ' + error.message);
    }
  };

  const handleEnrollmentSuccess = () => {
    setShowEnrollModal(false);
    loadEnrollmentData();
    showSuccess('Bus enrollment created successfully');
  };

  const handleRentalSuccess = () => {
    setShowRentalModal(false);
    loadRentalData();
    showSuccess('Trip rental created successfully');
  };

  const handleGroupSuccess = () => {
    setShowGroupModal(false);
    loadRentalData();
    showSuccess('Group saved successfully');
  };

  const handleBulkTransferSuccess = () => {
    setShowBulkTransferModal(false);
    loadEnrollmentData();
    showSuccess('Bulk transfer completed successfully');
  };

  // CLIENT-SIDE FILTERING - No API call, instant results
  const filteredEnrollments = useMemo(() => {
    let filtered = busEnrollments;
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(enrollment => 
        enrollment.StudentName?.toLowerCase().includes(term) ||
        enrollment.FatherName?.toLowerCase().includes(term) ||
        enrollment.StudentID?.toString().includes(term) ||
        enrollment.FamilyID?.toString().includes(term) ||
        enrollment.PrimaryPhone?.includes(term)
      );
    }
    
    // Filter by payment status
    if (filters.paymentStatus) {
      filtered = filtered.filter(enrollment => 
        enrollment.PaymentStatus === filters.paymentStatus
      );
    }
    
    return filtered;
  }, [busEnrollments, searchTerm, filters.paymentStatus]);

  const formatUSD = (amount) => {
    return '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Partial': return 'bg-yellow-100 text-yellow-800';
      case 'Unpaid': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'Suspended': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bus Management</h1>
            <p className="text-gray-600">Manage student enrollments and group rentals</p>
          </div>
          <div className="flex gap-3">
            <Gate adminOnly label="Live Bus Map">
            <button
              onClick={() => setShowLiveMap(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              title="Live map of every bus (position, speed, offline)"
            >
              <Navigation size={18} />
              Live Map
            </button>
            </Gate>
            <Gate adminOnly label="Drop-off Pins">
            <button
              onClick={() => setShowDropoffPins(true)}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
              title="View parent-set drop-off pins (family + bus)"
            >
              <MapPin size={18} />
              Drop-off Pins
            </button>
            </Gate>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="mt-6 border-b">
          <nav className="flex space-x-8">
            <Gate permission="bus.student_enrollments.view" label="Student Enrollments tab">
            <button
              onClick={() => setActiveTab('enrollments')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'enrollments'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-4 h-4" />
              Student Enrollments
            </button>
            </Gate>
            <Gate permission="bus.group_rentals.view" label="Group Rentals tab">
            <button
              onClick={() => setActiveTab('rentals')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'rentals'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Group Rentals
            </button>
            </Gate>
          </nav>
        </div>
      </div>

      {/* ========================================= */}
      {/* STUDENT ENROLLMENTS TAB */}
      {/* ========================================= */}
      {activeTab === 'enrollments' && canViewEnrollments && (
        <>
          {/* Sub-tabs for Student Enrollments */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                {[
                  { id: 'list', label: 'Enrollments', icon: Users },
                  { id: 'statistics', label: 'Statistics', icon: DollarSign, adminOnly: true },
                  { id: 'lines', label: 'Lines Overview', icon: BusIcon, adminOnly: true }
                ].map(tab => {
                  const tabButton = (
                  <button
                    onClick={() => setEnrollmentSubTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      enrollmentSubTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <tab.icon size={16} />
                    {tab.label}
                  </button>
                  );
                  return tab.adminOnly
                    ? <Gate key={tab.id} adminOnly label={`${tab.label} tab`}>{tabButton}</Gate>
                    : <React.Fragment key={tab.id}>{tabButton}</React.Fragment>;
                })}
              </div>
              <div className="flex gap-3">
                {enrollmentSubTab === 'list' && (
                  <>
                    <Gate permission="bus.balance_report.view" label="Balance Report">
                    <button
                      onClick={() => setShowBalanceReport(true)}
                      className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg"
                      title="View families with outstanding balances"
                    >
                      <FileText size={18} />
                      Balance Report
                    </button>
                    </Gate>
                    <Gate permission="bus.bus_roster.view" label="Bus Roster">
                    <button
                      onClick={() => setShowByBusReport(true)}
                      className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg"
                      title="Registered students grouped by bus (new riders flagged)"
                    >
                      <FileText size={18} />
                      Bus Roster
                    </button>
                    </Gate>
                    <Gate adminOnly label="Bulk Transfer">
                    <button
                      onClick={() => setShowBulkTransferModal(true)}
                      className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
                      title="Bulk transfer students to next trimester"
                    >
                      <ArrowRight size={18} />
                      Bulk Transfer
                    </button>
                    </Gate>
                  </>
                )}
                {enrollmentSubTab === 'list' && (
                  <Gate permission="bus.student_enrollments.create" label="New Enrollment">
                  <button 
                    onClick={() => setShowEnrollModal(true)} 
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                  >
                    <Plus size={18} />
                    New Enrollment
                  </button>
                  </Gate>
                )}
              </div>
            </div>
          </div>

          {/* Enrollments List Sub-tab */}
          {enrollmentSubTab === 'list' && (
            <>
              {/* Summary Cards */}
              {enrollmentStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 font-medium">Total Enrollments</p>
                        <p className="text-3xl font-bold text-gray-800 mt-2">{enrollmentStats.TotalEnrollments}</p>
                        <p className="text-xs text-gray-500 mt-1">{enrollmentStats.ActiveEnrollments} active</p>
                      </div>
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Users className="text-blue-600" size={24} />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 font-medium">Unique Students</p>
                        <p className="text-3xl font-bold text-gray-800 mt-2">{enrollmentStats.UniqueStudents}</p>
                        <p className="text-xs text-gray-500 mt-1">{enrollmentStats.UniqueFamilies} families</p>
                      </div>
                      <div className="bg-green-100 p-3 rounded-lg">
                        <Users className="text-green-600" size={24} />
                      </div>
                    </div>
                  </div>

                  {/* Total Revenue - Admin Only */}
                  <Gate adminOnly label="Total Revenue card">
                  {canBulkTransfer && (
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 font-medium">Total Revenue (net)</p>
                          <p className="text-3xl font-bold text-gray-800 mt-2">
                            ${enrollmentStats.TotalRevenue?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            ${enrollmentStats.TotalCollected?.toFixed(2) || '0.00'} collected
                          </p>
                          {enrollmentStats.TotalDiscount > 0 && (
                            <p className="text-xs text-amber-600 mt-0.5">
                              ${enrollmentStats.TotalGross?.toFixed(2) || '0.00'} gross − ${enrollmentStats.TotalDiscount?.toFixed(2) || '0.00'} discount
                            </p>
                          )}
                        </div>
                        <div className="bg-purple-100 p-3 rounded-lg">
                          <DollarSign className="text-purple-600" size={24} />
                        </div>
                      </div>
                    </div>
                  )}
                  </Gate>

                  {/* Outstanding - Admin Only */}
                  <Gate adminOnly label="Outstanding card">
                  {canBulkTransfer && (
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 font-medium">Outstanding</p>
                          <p className="text-3xl font-bold text-red-600 mt-2">
                            ${enrollmentStats.TotalOutstanding?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {enrollmentStats.UnpaidEnrollments} unpaid, {enrollmentStats.PartialEnrollments} partial
                          </p>
                        </div>
                        <div className="bg-red-100 p-3 rounded-lg">
                          <AlertCircle className="text-red-600" size={24} />
                        </div>
                      </div>
                    </div>
                  )}
                  </Gate>
                </div>
              )}

              {/* Filters */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Filter size={20} className="text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year</label>
                    <input
                      type="text"
                      value={filters.academicYear}
                      onChange={(e) => setFilters({...filters, academicYear: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="2024-2025"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Trimester</label>
                    <select
                      value={filters.trimester}
                      onChange={(e) => setFilters({...filters, trimester: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Trimesters</option>
                      <option value="1">Trimester 1</option>
                      <option value="2">Trimester 2</option>
                      <option value="3">Trimester 3</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Line</label>
                    <select
                      value={filters.lineId}
                      onChange={(e) => setFilters({...filters, lineId: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Lines</option>
                      {busLines.map(line => (
                        <option key={line.LineID} value={line.LineID}>
                          Line {line.LineNumber} - {line.LineName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({...filters, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Suspended">Suspended</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                    <select
                      value={filters.paymentStatus}
                      onChange={(e) => setFilters({...filters, paymentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Payments</option>
                      <option value="Paid">Paid</option>
                      <option value="Partial">Partial</option>
                      <option value="Unpaid">Unpaid</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Search size={16} className="inline mr-1" />
                      Search Student or Family
                    </label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Type to search instantly..."
                    />
                    {(searchTerm || filters.paymentStatus) && (
                      <p className="text-xs text-gray-500 mt-1">
                        Found {filteredEnrollments.length} of {busEnrollments.length} enrollments
                      </p>
                    )}
                  </div>

                  <div className="flex items-end gap-2">
                    <button
                      onClick={loadEnrollmentData}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                      title="Refresh data"
                    >
                      <RefreshCw size={18} />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              {/* Enrollments Table */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Student Bus Enrollments - {filters.academicYear}{filters.trimester && `, Trimester ${filters.trimester}`}
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-100 border-b">
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Student</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Class</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Family</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Line</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Bus #</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Trimester</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Price</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Discount</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Balance</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Payment</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Card</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEnrollments.map(enrollment => (
                        <tr key={enrollment.BusEnrollmentID} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-semibold text-gray-800">{enrollment.StudentName}</p>
                              <p className="text-sm text-gray-500">{enrollment.StudentID}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {enrollment.Class && enrollment.Section 
                              ? `${enrollment.Class} - ${enrollment.Section}`
                              : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-800">{enrollment.FatherName}</p>
                              <p className="text-sm text-gray-500">{enrollment.PrimaryPhone}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-800">Line {enrollment.LineNumber}</p>
                              <p className="text-sm text-gray-500">{enrollment.LineName}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {enrollment.HasDifferentBuses ? (
                              <div className="text-xs">
                                <div className="flex items-center gap-1 text-orange-600">
                                  <Sun size={12} />
                                  Morning: #{enrollment.MorningBusNumber}
                                </div>
                                <div className="flex items-center gap-1 text-indigo-600 mt-1">
                                  <Moon size={12} />
                                  Evening: #{enrollment.EveningBusNumber}
                                </div>
                              </div>
                            ) : (
                              <span>Bus #{enrollment.MorningBusNumber || enrollment.BusNumber}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-semibold">
                              T{enrollment.Trimester}
                            </span>
                          </td>
                          <td className="px-4 py-3">${(enrollment.TrimesterPrice || 0).toFixed(2)}</td>
                          <td className="px-4 py-3">
                            {enrollment.DiscountAmount > 0 ? (
                              <span className="text-green-600 font-semibold">
                                -${(enrollment.DiscountAmount || 0).toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-gray-400">$0.00</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-red-600 font-semibold">
                            ${(enrollment.BalanceRemaining || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getPaymentStatusColor(enrollment.PaymentStatus)}`}>
                              {enrollment.PaymentStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(enrollment.Status || 'Active')}`}>
                              {enrollment.Status || 'Active'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {enrollment.CardPrinted ? (
                              <span className="flex items-center gap-1 text-green-600 text-sm">
                                <CreditCard size={14} />
                                Printed
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">Not Printed</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewDetails(enrollment)}
                                className="text-blue-600 hover:text-blue-800"
                                title="View Details"
                              >
                                <Eye size={18} />
                              </button>
                              {!enrollment.CardPrinted && (
                                <Gate permission="bus.print_card.print" label="Print Card">
                                <button 
                                  onClick={() => handlePrintCard(enrollment.BusEnrollmentID)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Print card"
                                >
                                  <Printer size={18} />
                                </button>
                                </Gate>
                              )}
                              {/* Admin-only: edit line/price, buses, addresses
                                  and discount for THIS trimester row. */}
                              {canBulkTransfer && (enrollment.Status || 'Active') === 'Active' && (
                                <button
                                  onClick={() => setEditEnrollment(enrollment)}
                                  className="text-indigo-600 hover:text-indigo-800"
                                  title="Edit enrollment (line, buses, address, discount)"
                                >
                                  <Pencil size={18} />
                                </button>
                              )}
                              {(enrollment.Status || 'Active') === 'Active' && (
                                <Gate permission="bus.student_enrollments.edit" label="Cancel Enrollment">
                                <button
                                  onClick={() => handleCancelEnrollment(enrollment.BusEnrollmentID)}
                                  className="text-red-600 hover:text-red-800"
                                  title="Cancel Enrollment"
                                >
                                  <XCircle size={18} />
                                </button>
                                </Gate>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredEnrollments.length === 0 && (
                        <tr>
                          <td colSpan="13" className="px-4 py-8 text-center text-gray-500">
                            {searchTerm ? `No enrollments found matching "${searchTerm}"` : 'No enrollments found'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Statistics Sub-tab */}
          {enrollmentSubTab === 'statistics' && enrollmentStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Enrollment Overview</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Enrollments:</span>
                    <span className="font-semibold text-gray-800">{enrollmentStats.TotalEnrollments}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Active:</span>
                    <span className="font-semibold text-green-600">{enrollmentStats.ActiveEnrollments}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Cancelled:</span>
                    <span className="font-semibold text-red-600">{enrollmentStats.CancelledEnrollments || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Unique Students:</span>
                    <span className="font-semibold text-gray-800">{enrollmentStats.UniqueStudents}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Unique Families:</span>
                    <span className="font-semibold text-gray-800">{enrollmentStats.UniqueFamilies}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Financial Overview</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Gross Charges:</span>
                    <span className="font-semibold text-gray-800">${enrollmentStats.TotalGross?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Discounts:</span>
                    <span className="font-semibold text-amber-600">−${enrollmentStats.TotalDiscount?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Revenue (net):</span>
                    <span className="font-semibold text-gray-800">${enrollmentStats.TotalRevenue?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Collected:</span>
                    <span className="font-semibold text-green-600">${enrollmentStats.TotalCollected?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Outstanding:</span>
                    <span className="font-semibold text-red-600">${enrollmentStats.TotalOutstanding?.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Paid Enrollments:</span>
                      <span className="text-green-600">{enrollmentStats.PaidEnrollments}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-2">
                      <span className="text-gray-600">Partial Payments:</span>
                      <span className="text-yellow-600">{enrollmentStats.PartialEnrollments}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-2">
                      <span className="text-gray-600">Unpaid:</span>
                      <span className="text-red-600">{enrollmentStats.UnpaidEnrollments}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Card Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Cards Printed:</span>
                    <span className="font-semibold text-green-600">{enrollmentStats.CardsPrinted}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Pending:</span>
                    <span className="font-semibold text-gray-800">
                      {enrollmentStats.ActiveEnrollments - enrollmentStats.CardsPrinted}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Lines Overview Sub-tab */}
          {enrollmentSubTab === 'lines' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Line</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Route</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Price/Trimester</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Enrollments</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Active</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Discount</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Revenue</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {lineStats.map((line) => (
                      <tr key={line.LineID} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-semibold text-gray-800">Line {line.LineNumber}</p>
                            <p className="text-sm text-gray-500">{line.LineName}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {line.RouteDescription || '-'}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-gray-800">
                          ${line.TrimesterPrice?.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-right text-gray-800">
                          {line.TotalEnrollments || 0}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-green-600">
                          {line.ActiveEnrollments || 0}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-amber-600">
                          {line.TotalDiscount > 0 ? `−$${line.TotalDiscount.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-gray-800">
                          ${line.TotalRevenue?.toFixed(2) || '0.00'}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-red-600">
                          ${line.TotalOutstanding?.toFixed(2) || '0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========================================= */}
      {/* GROUP RENTALS TAB */}
      {/* ========================================= */}
      {activeTab === 'rentals' && canViewRentals && (
        <>
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Gate permission="bus.rental_groups.edit" label="Manage Groups">
            <button
              onClick={() => setShowGroupModal(true)}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
            >
              <Users size={18} />
              Manage Groups
            </button>
            </Gate>
            <Gate permission="bus.group_rentals.create" label="New Trip Rental">
            <button
              onClick={() => setShowRentalModal(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              <Plus size={18} />
              New Trip Rental
            </button>
            </Gate>
          </div>

          {/* Groups Summary Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            {rentalGroups.map(group => (
              <div 
                key={group.GroupID}
                onClick={() => setSelectedGroup(group.GroupID === selectedGroup ? null : group.GroupID)}
                className={`bg-white rounded-lg border-2 p-4 cursor-pointer transition-all ${
                  selectedGroup === group.GroupID 
                    ? 'border-blue-500 shadow-lg' 
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">{group.GroupName}</h3>
                </div>
                <p className="text-sm text-gray-600 mb-1">{group.ContactPerson}</p>
                <p className="text-xs text-gray-500">{group.ContactPhone}</p>
                <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Total Trips:</span>
                    <span className="ml-1 font-semibold">{group.TotalTrips || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Revenue:</span>
                    <span className="ml-1 font-semibold text-green-600">
                      {formatUSD(group.TotalRevenue || 0)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {rentalGroups.length === 0 && (
              <div className="col-span-3 text-center py-8 text-gray-500">
                No groups yet. Click "Manage Groups" to add one.
              </div>
            )}
          </div>

          {/* Trips Table */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {selectedGroup 
                ? `Trips for ${rentalGroups.find(g => g.GroupID === selectedGroup)?.GroupName}`
                : 'All Trips'}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Group</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Destination</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Buses</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Passengers</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Price</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Paid</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Payment</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rentalTrips
                    .filter(trip => !selectedGroup || trip.GroupID === selectedGroup)
                    .map(trip => (
                      <tr key={trip.TripID} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">{new Date(trip.TripDate).toLocaleDateString()}</td>
                        <td className="px-4 py-3">{trip.GroupName}</td>
                        <td className="px-4 py-3">{trip.Destination}</td>
                        <td className="px-4 py-3 text-center">{trip.NumberOfBuses}</td>
                        <td className="px-4 py-3 text-center">{trip.NumberOfPassengers || '-'}</td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {formatUSD(trip.FinalPrice)}
                          {trip.DiscountPercent > 0 && (
                            <span className="ml-1 text-xs text-green-600">
                              (-{trip.DiscountPercent}%)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-green-600">
                          {formatUSD(trip.AmountPaid)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs ${
                            trip.PaymentStatus === 'Paid' ? 'bg-green-100 text-green-800' :
                            trip.PaymentStatus === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {trip.PaymentStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs ${
                            trip.TripStatus === 'Completed' ? 'bg-blue-100 text-blue-800' :
                            trip.TripStatus === 'Scheduled' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {trip.TripStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  {rentalTrips.filter(trip => !selectedGroup || trip.GroupID === selectedGroup).length === 0 && (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                        No trips found. Click "New Trip Rental" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Revenue Summary */}
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Total Trips</p>
              <p className="text-2xl font-bold text-gray-900">
                {rentalTrips.filter(t => !selectedGroup || t.GroupID === selectedGroup).length}
              </p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">
                {formatUSD(
                  rentalTrips
                    .filter(t => !selectedGroup || t.GroupID === selectedGroup)
                    .reduce((sum, t) => sum + (t.FinalPrice || 0), 0)
                )}
              </p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Amount Collected</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatUSD(
                  rentalTrips
                    .filter(t => !selectedGroup || t.GroupID === selectedGroup)
                    .reduce((sum, t) => sum + (t.AmountPaid || 0), 0)
                )}
              </p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Outstanding</p>
              <p className="text-2xl font-bold text-red-600">
                {formatUSD(
                  rentalTrips
                    .filter(t => !selectedGroup || t.GroupID === selectedGroup)
                    .reduce((sum, t) => sum + ((t.FinalPrice || 0) - (t.AmountPaid || 0)), 0)
                )}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {showEnrollModal && (
        <BusEnrollModal
          apiCall={apiCall}
          selectedYear={filters.academicYear}
          currentTrimester={parseInt(filters.trimester) || currentTrimester}
          busLines={busLines}
          buses={buses}
          onClose={() => setShowEnrollModal(false)}
          onSuccess={handleEnrollmentSuccess}
          showError={showError}
        />
      )}

      {showRentalModal && (
        <BusRentalModal
          apiCall={apiCall}
          buses={buses}
          groups={rentalGroups}
          onClose={() => setShowRentalModal(false)}
          onSuccess={handleRentalSuccess}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {showGroupModal && (
        <GroupManagementModal
          apiCall={apiCall}
          groups={rentalGroups}
          onClose={() => setShowGroupModal(false)}
          onSuccess={handleGroupSuccess}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {/* Details Modal */}
      {detailsModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-800">Enrollment Details</h3>
              <button
                onClick={() => setDetailsModal({ open: false, data: null })}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {detailsModal.data && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Student</p>
                    <p className="text-gray-800 font-semibold">{detailsModal.data.StudentName}</p>
                    <p className="text-sm text-gray-500">{detailsModal.data.StudentID}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Class</p>
                    <p className="text-gray-800">
                      {detailsModal.data.Class && detailsModal.data.Section
                        ? `${detailsModal.data.Class} - ${detailsModal.data.Section}`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Father</p>
                    <p className="text-gray-800">{detailsModal.data.FatherName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Phone</p>
                    <p className="text-gray-800">{detailsModal.data.PrimaryPhone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Line</p>
                    <p className="text-gray-800 font-semibold">
                      Line {detailsModal.data.LineNumber} - {detailsModal.data.LineName}
                    </p>
                    <p className="text-sm text-gray-500">{detailsModal.data.RouteDescription}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600 font-medium mb-2">Service Type</p>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r">
                      <p className="text-gray-800 font-semibold">
                        {detailsModal.data.BusService === 'Both' && '🌅🌙 Both Ways (Morning & Evening)'}
                        {detailsModal.data.BusService === 'MorningOnly' && '🌅 Morning Only (To School)'}
                        {detailsModal.data.BusService === 'EveningOnly' && '🌙 Evening Only (From School)'}
                        {!detailsModal.data.BusService && '🌅🌙 Both Ways (Morning & Evening)'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600 font-medium mb-2">Bus Configuration</p>
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-3 rounded-r">
                      {(detailsModal.data.BusService === 'Both' || !detailsModal.data.BusService) && detailsModal.data.HasDifferentBuses ? (
                        <div className="space-y-2">
                          <p className="text-gray-800 font-semibold flex items-center gap-2">
                            <Sun size={16} className="text-orange-500" />
                            Morning Bus: #{detailsModal.data.MorningBusNumber}
                          </p>
                          <p className="text-gray-800 font-semibold flex items-center gap-2">
                            <Moon size={16} className="text-indigo-500" />
                            Evening Bus: #{detailsModal.data.EveningBusNumber}
                          </p>
                        </div>
                      ) : detailsModal.data.BusService === 'MorningOnly' ? (
                        <p className="text-gray-800 font-semibold flex items-center gap-2">
                          <Sun size={16} className="text-orange-500" />
                          Morning Bus: #{detailsModal.data.MorningBusNumber}
                        </p>
                      ) : detailsModal.data.BusService === 'EveningOnly' ? (
                        <p className="text-gray-800 font-semibold flex items-center gap-2">
                          <Moon size={16} className="text-indigo-500" />
                          Evening Bus: #{detailsModal.data.EveningBusNumber}
                        </p>
                      ) : (
                        <p className="text-gray-800 font-semibold">
                          Bus #{detailsModal.data.MorningBusNumber || detailsModal.data.BusNumber}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="col-span-2">
                    <p className="text-sm text-gray-600 font-medium mb-2">Pickup/Dropoff Address</p>
                    <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r">
                      {detailsModal.data.UseCustomAddress ? (
                        detailsModal.data.HasDifferentAddresses ? (
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-gray-600 font-medium mb-1 flex items-center gap-1">
                                <Sun size={12} className="text-orange-500" />
                                Morning Pickup Address:
                              </p>
                              <p className="text-gray-800">{detailsModal.data.MorningAddress}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 font-medium mb-1 flex items-center gap-1">
                                <Moon size={12} className="text-indigo-500" />
                                Evening Dropoff Address:
                              </p>
                              <p className="text-gray-800">{detailsModal.data.EveningAddress}</p>
                            </div>
                            {detailsModal.data.AddressNotes && (
                              <div className="pt-2 border-t border-green-200">
                                <p className="text-xs text-gray-600 font-medium mb-1">Driver Notes:</p>
                                <p className="text-gray-700 italic text-sm">{detailsModal.data.AddressNotes}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <p className="text-xs text-gray-600 font-medium mb-1">Custom Address:</p>
                            <p className="text-gray-800">{detailsModal.data.MorningAddress || detailsModal.data.EveningAddress}</p>
                            {detailsModal.data.AddressNotes && (
                              <div className="pt-2 border-t border-green-200 mt-2">
                                <p className="text-xs text-gray-600 font-medium mb-1">Driver Notes:</p>
                                <p className="text-gray-700 italic text-sm">{detailsModal.data.AddressNotes}</p>
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        <div>
                          <p className="text-xs text-gray-600 font-medium mb-1">Using Family Address:</p>
                          <p className="text-gray-800">{detailsModal.data.FamilyAddress || detailsModal.data.Address || 'Not specified'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Academic Year</p>
                    <p className="text-gray-800">{detailsModal.data.AcademicYear}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Trimester</p>
                    <p className="text-gray-800">Trimester {detailsModal.data.Trimester}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Price</p>
                    <p className="text-gray-800 font-semibold">
                      ${detailsModal.data.TrimesterPrice?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Amount Paid</p>
                    <p className="text-green-600 font-semibold">
                      ${detailsModal.data.AmountPaid?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Balance</p>
                    <p className="text-red-600 font-semibold">
                      ${detailsModal.data.BalanceRemaining?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Payment Status</p>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getPaymentStatusColor(detailsModal.data.PaymentStatus)}`}>
                      {detailsModal.data.PaymentStatus}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Enrollment Status</p>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getStatusColor(detailsModal.data.Status || 'Active')}`}>
                      {detailsModal.data.Status || 'Active'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Card Status</p>
                    <p className="text-gray-800">
                      {detailsModal.data.CardPrinted ? (
                        <span className="text-green-600">Printed</span>
                      ) : (
                        <span className="text-gray-400">Not Printed</span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Gate permission="bus.print_card.print" label="Print Card (details)">
              <button
                onClick={() => handlePrintCard(detailsModal.data.BusEnrollmentID)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2"
              >
                <Printer size={18} />
                Print Card
              </button>
              </Gate>
              <button
                onClick={() => setDetailsModal({ open: false, data: null })}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-lg font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bus Card Print Modal */}
      {printCardModal.open && printCardModal.data && (
        <BusCardPrintModal
          enrollment={printCardModal.data}
          onClose={() => setPrintCardModal({ open: false, data: null })}
          onPrinted={() => handleCardPrinted(printCardModal.data.BusEnrollmentID)}
        />
      )}

      {/* Bulk Trimester Transfer Modal */}
      {showBulkTransferModal && (
        <BulkTrimesterTransferModal
          apiCall={apiCall}
          selectedYear={selectedYear}
          currentTrimester={currentTrimester}
          onClose={() => setShowBulkTransferModal(false)}
          onSuccess={handleBulkTransferSuccess}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {/* Balance Report Modal */}
      <BusBalanceReportModal
        isOpen={showBalanceReport}
        onClose={() => setShowBalanceReport(false)}
        apiCall={apiCall}
        selectedYear={selectedYear}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Bus Roster (by bus) Report Modal */}
      <BusByBusReportModal
        isOpen={showByBusReport}
        onClose={() => setShowByBusReport(false)}
        apiCall={apiCall}
        selectedYear={selectedYear}
        currentTrimester={currentTrimester}
        showError={showError}
      />

      {/* Drop-off Pins Modal (admin) */}
      <BusDropoffPinsModal
        isOpen={showDropoffPins}
        onClose={() => setShowDropoffPins(false)}
        apiCall={apiCall}
        showError={showError}
      />

      <BusLiveMapModal
        isOpen={showLiveMap}
        onClose={() => setShowLiveMap(false)}
        apiCall={apiCall}
        showError={showError}
      />

      {/* Admin enrolment edit */}
      {editEnrollment && (
        <BusEditEnrollmentModal
          enrollment={editEnrollment}
          lines={busLines}
          apiCall={apiCall}
          onClose={() => setEditEnrollment(null)}
          onSuccess={(result) => {
            setEditEnrollment(null);
            showSuccess(result?.message || 'Enrollment updated');
            loadEnrollmentData();
          }}
          showError={showError}
        />
      )}
    </div>
  );
};

export default Bus;